/*
 * Note: This is non-end-user facing code so no need for i18n strings.
 */

import { Button, IconButton } from "@dropbox/dig-components/buttons";
import { Text } from "@dropbox/dig-components/typography";
import { Box } from "@dropbox/dig-foundations";
import { UIIcon } from "@dropbox/dig-icons";
import { InfoLine } from "@dropbox/dig-icons/dist/mjs/assets";
import MagicFlagSettings from "@mirage/mosaics/MagicFlags/MagicFlagsSettings";
import { EnvCtx } from "@mirage/service-environment-context/global-env-ctx";
import {
  clearClientKeyOverride,
  listenForClientKeyOverride,
  refreshAllFeatureValues,
  resetAllFeatureOverrides,
  setClientKeyOverride,
} from "@mirage/service-experimentation";
import { features } from "@mirage/service-experimentation/features";
import { useFeatureFlags } from "@mirage/service-experimentation/useInitFeatureFlags";
import { useFeatureRingSettings } from "@mirage/service-feature-ring-settings/hooks/useFeatureRingSettings";
import {
  GROWTHBOOK_API_KEY_DEVBOX,
  GROWTHBOOK_API_KEY_PROD,
} from "@mirage/shared/util/build-vars";
import { DigTooltip } from "@mirage/shared/util/DigTooltip";
import sortBy from "lodash/sortBy";
import { useEffect, useMemo, useState } from "react";
import { FeatureList } from "./FeatureList";
import { FeatureRingPicker } from "./FeatureRingPicker";
import styles from "./FeaturesSettings.module.css";

export default function FeaturesSettings() {
  const [clientKeyOverride, setClientKeyOverrideState] = useState<
    string | undefined
  >(undefined);
  const featureFlags = useFeatureFlags();
  // Load persisted GrowthBook clientKey override
  useEffect(() => {
    listenForClientKeyOverride().subscribe((key) => {
      setClientKeyOverrideState(key);
    });
  }, []);
  const sortedFeatures = useMemo(() => {
    const f = Object.values(features);
    return sortBy(f, ({ featureName }) => featureName);
  }, []);
  // Handlers for GrowthBook clientKey
  const handleSetClientKey = async (key: string) => {
    await setClientKeyOverride(key);
    setClientKeyOverrideState(key);
    await refreshAllFeatureValues();
  };
  const handleClearClientKey = async () => {
    await clearClientKeyOverride();
    setClientKeyOverrideState(undefined);
    await refreshAllFeatureValues();
  };
  const isClientKeyOverridden = clientKeyOverride !== undefined;
  // Determine current GrowthBook environment label
  const currentEnvLabel = clientKeyOverride
    ? clientKeyOverride === GROWTHBOOK_API_KEY_DEVBOX
      ? "Devbox"
      : "Production"
    : EnvCtx.environment === "development"
      ? "Devbox"
      : "Production";

  const featuresWithOverride = useMemo(
    () =>
      sortedFeatures.filter(
        ({ featureName }) =>
          featureFlags[featureName].overrideValue !== undefined,
      ),
    [featureFlags, sortedFeatures],
  );

  const {
    featureRingSettings,
    getCurrentFeatureRing,
    clearOverride,
    setOveride,
    isOveridden,
  } = useFeatureRingSettings();

  useEffect(() => {
    getCurrentFeatureRing();
  }, [getCurrentFeatureRing]);

  return (
    <div className={styles.wrapper}>
      {/* GrowthBook Client Key Override Section */}
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Text size="medium" isBold>
          GrowthBook Client ID
        </Text>
        <Box>
          <Button
            variant="outline"
            onClick={handleClearClientKey}
            disabled={!isClientKeyOverridden}
          >
            Reset to default
          </Button>
        </Box>
      </Box>
      <hr className={styles.horizontalSeparator} />
      <Box display="flex">
        <Box paddingRight="8">
          <Button
            variant={currentEnvLabel === "Production" ? "primary" : "outline"}
            onClick={() => handleSetClientKey(GROWTHBOOK_API_KEY_PROD)}
          >
            Production
          </Button>
        </Box>
        <Button
          variant={currentEnvLabel === "Devbox" ? "primary" : "outline"}
          onClick={() => handleSetClientKey(GROWTHBOOK_API_KEY_DEVBOX)}
        >
          Devbox
        </Button>

        <DigTooltip
          openDelay={0}
          title={
            <Box
              display="flex"
              flexDirection="column"
              paddingBottom="20"
              paddingLeft="12"
              paddingRight="12"
            >
              <Text>
                The Growthbook client ID controls which set of Growthbook rules
                are applied.
              </Text>
              <Box paddingTop="12">
                <Box paddingTop="8">
                  <Text isBold>Devbox</Text>
                  <Text>&nbsp; - used when running locally via yarn</Text>
                </Box>
                <Box paddingTop="8">
                  <Text isBold>Production</Text>
                  <Text>&nbsp; - used any time the app is compiled</Text>
                </Box>
              </Box>
            </Box>
          }
        >
          <IconButton variant="transparent">
            <UIIcon src={InfoLine} />
          </IconButton>
        </DigTooltip>
      </Box>
      <Box paddingBottom="16" paddingTop="32">
        <Text>
          Current environment: {currentEnvLabel}
          <Text isBold={isClientKeyOverridden}>
            &nbsp; ({isClientKeyOverridden ? "overridden" : "default"})
          </Text>
        </Text>
      </Box>
      {/* End GrowthBook Section */}

      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Text size="medium" isBold>
          Feature Ring
        </Text>
        <Box>
          <Button
            variant="outline"
            onClick={async () => {
              await clearOverride();
              refreshAllFeatureValues();
            }}
            disabled={!isOveridden()}
          >
            Reset to default
          </Button>
        </Box>
      </Box>
      <hr className={styles.horizontalSeparator} />
      <FeatureRingPicker
        featureFlags={featureFlags}
        featureRingSettings={featureRingSettings}
        onSetOverride={async (value) => {
          await setOveride(value);
          await refreshAllFeatureValues();
        }}
      ></FeatureRingPicker>

      {featuresWithOverride.length > 0 && (
        <>
          <Box paddingBottom="16" paddingTop="32">
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Text size="medium" isBold>
                Active Feature Flag Overrides
              </Text>
              <Box>
                <Button
                  variant="outline"
                  onClick={async () => {
                    await resetAllFeatureOverrides();
                  }}
                >
                  Clear all overrides
                </Button>
              </Box>
            </Box>

            <hr className={styles.horizontalSeparator} />
          </Box>
          <FeatureList sortedFeatures={featuresWithOverride} />
        </>
      )}

      <Box display="block" paddingBottom="16" paddingTop="32">
        <Text size="medium" isBold>
          Feature Flags
        </Text>
        <hr className={styles.horizontalSeparator} />
      </Box>
      <FeatureList sortedFeatures={sortedFeatures} />
      <Box display="block" paddingBottom="16" paddingTop="32">
        <Text size="medium" isBold>
          Magic Flags
        </Text>
        <hr className={styles.horizontalSeparator} />
      </Box>
      <MagicFlagSettings />
    </div>
  );
}
